import type { Capability } from "./types";

export const CAPABILITIES: Record<string, Capability> = {
  kids_checkin: {
    key: "kids_checkin", label: "Kids Check-In",
    description: "Manage child check-in/check-out for services and events",
    category: "ministry", tier: "harvest",
    toggle: { default: true, userToggleable: true },
  },
  volunteer_scheduling: {
    key: "volunteer_scheduling", label: "Volunteer Scheduling",
    description: "Schedule and manage volunteer teams for services and events",
    category: "ministry", tier: "harvest",
    toggle: { default: true, userToggleable: true },
  },
  prayer_wall: {
    key: "prayer_wall", label: "Prayer Wall",
    description: "Public prayer request wall on the Member Portal",
    category: "engagement", tier: "plant",
    toggle: { default: true, userToggleable: true },
    portal: { visible: true },
  },
  online_giving: {
    key: "online_giving", label: "Online Giving",
    description: "Accept contributions through the Member Portal",
    category: "finance", tier: "plant",
    toggle: { default: true, userToggleable: true },
    portal: { visible: true },
  },
  member_directory: {
    key: "member_directory", label: "Member Directory",
    description: "Searchable member directory on the Member Portal",
    category: "engagement", tier: "plant",
    toggle: { default: true, userToggleable: true },
    portal: { visible: true },
  },
  groups: {
    key: "groups", label: "Groups & Small Groups",
    description: "Manage small groups, Bible studies, and recurring gatherings",
    category: "engagement", tier: "harvest",
    toggle: { default: true, userToggleable: true },
    portal: { visible: true },
  },
  projects_tasks: {
    key: "projects_tasks", label: "Projects & Tasks",
    description: "Project management with task tracking and assignments",
    category: "operations", tier: "harvest",
    toggle: { default: true, userToggleable: true },
  },
  asset_management: {
    key: "asset_management", label: "Asset Management",
    description: "Track physical resources, rooms, and equipment",
    category: "operations", tier: "harvest",
    toggle: { default: true, userToggleable: true },
  },
  hr_staff: {
    key: "hr_staff", label: "HR & Staff Management",
    description: "Staff directory, recruitment, onboarding, and HR tracking",
    category: "operations", tier: "flourish",
    toggle: { default: true, userToggleable: true },
  },
  accounting: {
    key: "accounting", label: "Accounting Module",
    description: "Full GAAP/ASC 958 accounting with GL, AP/AR, and financial statements",
    category: "finance", tier: "flourish",
    toggle: { default: true, userToggleable: true },
  },
  pledge_management: {
    key: "pledge_management", label: "Pledge Management",
    description: "Create and track pledge campaigns with member commitments",
    category: "finance", tier: "harvest",
    toggle: { default: true, userToggleable: true },
    portal: { visible: true },
  },
  sms_messaging: {
    key: "sms_messaging", label: "SMS Messaging",
    description: "Send SMS messages to members and groups",
    category: "communications", tier: "harvest",
    toggle: { default: true, userToggleable: true },
  },
  engagement_dashboards: {
    key: "engagement_dashboards", label: "Engagement Dashboards",
    description: "Visual dashboards showing member engagement patterns and trends",
    category: "engagement", tier: "harvest",
    toggle: { default: true, userToggleable: true },
  },
  // ── Section parent for Ministry (not itself toggleable) ──
  ministry: {
    key: "ministry", label: "Ministry", description: "Services, volunteers, attendance",
    category: "ministry",
    // nav `order` values intentionally leave gaps so Task 8 can slot ministry children
    // (services, volunteers, attendance, etc.) between the section parent (0) and sermons (60)
    // without renumbering.
    nav: { section: "ministry", order: 0, href: "/ministry",
      // NOTE (Task 8): these prefixes overlap with kids_checkin/volunteer_scheduling/groups once those get their own nav; active-route matching must prefer most-specific, or trim this list.
      matchPrefixes: ["/ministry", "/services", "/volunteers", "/groups", "/attendance", "/kids-check-in"] },
  },
  "ministry.sermons": {
    key: "ministry.sermons", label: "Sermons",
    description: "Sermon library and series management",
    category: "ministry", parent: "ministry",
    toggle: { default: true, userToggleable: true },
    nav: { section: "ministry", order: 60, href: "/sermons", matchPrefixes: ["/sermons"] },
    portal: { visible: true },
  },
  "hr_staff.performance_reviews": {
    key: "hr_staff.performance_reviews", label: "Performance Reviews",
    description: "Staff performance review cycles and templates",
    category: "operations", parent: "hr_staff", tier: "flourish",
    toggle: { default: true, userToggleable: true },
  },
  "accounting.advanced": {
    key: "accounting.advanced", label: "Advanced Accounting",
    description: "Multi-entity consolidation, allocations, and advanced reporting",
    category: "finance", parent: "accounting", tier: "flourish",
    toggle: { default: false, userToggleable: true },
  },
};

/** Stable ordered list of capability keys. */
export const CAPABILITY_KEYS = Object.keys(CAPABILITIES);
